"use client";

import { useEffect, useState } from "react";
import Image from "next/image";

/**
 * Theme-aware Instanodes logo. Renders a single <Image>; swaps `src` based on
 * the current `data-theme` attribute. Avoids the dual-image flash and fixes
 * the duplicate-render bug seen with display:none toggling.
 */
interface LogoProps {
  width?: number;
  height?: number;
  className?: string;
  priority?: boolean;
}

// Intrinsic SVG dimensions: 269x51 (ratio 5.275:1). Width/height props match
// that ratio so next/image computes the correct intrinsic aspect; CSS class
// controls the actual displayed size.
export default function Logo({
  width = 169,
  height = 32,
  className = "h-8 w-auto",
  priority = false,
}: LogoProps) {
  const [isLight, setIsLight] = useState(false);

  useEffect(() => {
    const root = document.documentElement;
    const update = () =>
      setIsLight(root.getAttribute("data-theme") === "light");
    update();
    const obs = new MutationObserver(update);
    obs.observe(root, { attributes: true, attributeFilter: ["data-theme"] });
    return () => obs.disconnect();
  }, []);

  const src = isLight
    ? "/images/logos/instanodes-light.svg"
    : "/images/logos/instanodes.svg";

  return (
    <Image
      key={src}
      src={src}
      alt="Instanodes"
      width={width}
      height={height}
      priority={priority}
      className={className}
    />
  );
}
